<script setup>
import { nextTick, onMounted, shallowRef, useTemplateRef } from 'vue'
import ClassicAuthCard from '../components/ClassicAuthCard.vue'

const props = defineProps({
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  registerUrl: { type: String, default: '/register' },
  termsLink: { type: String, default: '' },
  privacyLink: { type: String, default: '' },
  phoneOnly: { type: Boolean, default: false },
  clearOnReload: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'email', 'clearError'])
const identifier = shallowRef('')
const identifierInput = useTemplateRef('identifierInput')

onMounted(() => {
  if (!props.clearOnReload) return

  nextTick(() => {
    identifier.value = ''
    if (identifierInput.value) identifierInput.value.value = ''
  })
})

function submit() {
  if (props.loading || !identifier.value.trim()) return
  emit('submit', identifier.value.trim())
}
</script>

<template>
  <ClassicAuthCard title="Login" :register-url="registerUrl">
    <form
      class="classic-login"
      :autocomplete="clearOnReload ? 'off' : 'on'"
      @submit.prevent="submit"
    >
      <label class="classic-login__label" for="classic-user-identifier">
        {{ phoneOnly ? 'Mobile Phone *' : 'Email or Mobile Phone *' }}
      </label>
      <input
        id="classic-user-identifier"
        ref="identifierInput"
        v-model="identifier"
        class="classic-login__input"
        :type="phoneOnly ? 'tel' : 'text'"
        :autocomplete="clearOnReload ? 'off' : phoneOnly ? 'tel' : 'username'"
        required
        @input="emit('clearError')"
      >

      <p v-if="error" class="classic-login__error" role="alert">{{ error }}</p>
      <div v-if="phoneOnly" class="classic-login__links">
        <button type="button" :disabled="loading" @click="emit('email')">Login with email</button>
      </div>
      <p class="classic-login__terms">
        By continuing, you agree with the
        <a v-if="termsLink" :href="termsLink" target="_blank" rel="noopener">Terms of Service</a>,
        acknowledge the
        <a v-if="privacyLink" :href="privacyLink" target="_blank" rel="noopener">Privacy Policy</a>
        and agree to receive text messages from
        <a href="https://remonihealth.com/" target="_blank" rel="noopener">RemoniHealth</a>.
      </p>
      <button class="classic-login__submit" type="submit" :disabled="loading || !identifier">
        {{ loading ? 'Please wait…' : phoneOnly ? 'Sent code' : 'Login' }}
      </button>
    </form>
  </ClassicAuthCard>
</template>

<style scoped>
.classic-login { display: flex; flex-direction: column; }
.classic-login__label { color: #333; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
.classic-login__input { background: transparent; border: 0; border-bottom: 1px solid #c6c6c6; border-radius: 0; color: #7c7c7c; font: inherit; min-height: 46px; padding: 10px 0; }
.classic-login__input:focus { border-color: #ff7f5d; box-shadow: none; outline: none; }
.classic-login__error { color: #dc3545; font-size: 13px; margin: 8px 0 0; }
.classic-login__links { display: flex; justify-content: flex-end; margin: 15px 0 0; }
.classic-login__links button { background: none; border: 0; color: #564495; cursor: pointer; font: inherit; font-size: 14px; padding: 0; }
.classic-login__links button:disabled { cursor: not-allowed; opacity: 0.6; }
.classic-login__terms { color: #7c7c7c; font-size: 14px; line-height: 1.5; margin: 18px 0; text-align: center; }
.classic-login__terms a { color: #564495; text-decoration: none; }
.classic-login__submit { align-self: center; background: #ff7f5d; border: 0; border-radius: 25px; color: #fff; cursor: pointer; font: inherit; font-weight: 500; min-width: 230px; padding: 12px 20px; }
.classic-login__submit:disabled { cursor: not-allowed; opacity: 0.6; }
</style>
